import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthLayout } from 'src/components/Layout/AuthLayout';
import { VisualSideLayout } from 'src/components/Layout/VisualsLayout';
import { SignUpForm } from 'src/components/Forms/Signup';
import { authService } from 'src/services/services/auth/auth.api';
import { ResponseKind } from 'src/services/services/MainService';
import {
  ErrorKind,
  isClientError,
} from 'src/services/services/MainService/apiProblem';
import { authRoutes } from 'src/navigators/routes/auth';
import { sendSuccessNotification } from 'src/utils/notifications';
import { NotificationTypes } from 'src/enums/Notifications';
import useResponsive from 'src/hooks/useResponsive';
import { Notifications } from '@mantine/notifications';
import { Alert } from '@mantine/core';
import FeedbackModal from 'src/components/Modals/FeedbackModal';
import { ErrorMessage } from 'src/components/common/ErrorMessage/ErrorMessage';
import { ConfirmFeedbackModal } from 'src/components/Modals/ConfirmationModal';
import { FeedbackMessage } from 'src/components/common/FeedbackMessage/FeedbackMessage';
import { PageTransition } from 'src/components/common/PageTransition/PageTransition';

const SignUp: React.FC = () => {
  const navigation = useNavigate();
  const { isMobile } = useResponsive();
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState({ message: '', title: '' });
  const [error, setError] = useState('');

  const navigateToSignin = useCallback(() => {
    navigation('/auth/login');
  }, []);

  useEffect(() => {
    if (feedback.message) {
      const handleLogin = () => {
        setFeedback({ message: '', title: '' });
        navigateToSignin();
      };
      setTimeout(handleLogin, 4000);
    }
    return;
  }, [feedback]);

  const signUp = async (values: any) => {
    setLoading(true);
    setError('');

    try {
      const response = await authService.signUp(
        values.firstName + ' ' + values.lastName,
        values.email,
        values.password,
        values.phone
      );

      if (response.kind === ResponseKind.OK) {
        // Success case - user created
        setFeedback({
          message:
            'We have sent an account activation email, please verify your account',
          title: 'User created successfully',
        });
      } else if ('errors' in response) {
        // Handle error response with error details
        const errorMessage = Array.isArray(response.errors)
          ? response.errors.join(' ')
          : typeof response.errors === 'string'
          ? response.errors
          : 'An error occurred during sign up';

        setError(errorMessage);
        throw new Error(errorMessage);
      } else {
        // Handle other error cases
        const errorMessage = 'An unexpected error occurred. Please try again.';
        setError(errorMessage);
        throw new Error(errorMessage);
      }
    } catch (e: unknown) {
      if (e instanceof Error) {
        setError(e.message);
      } else if (typeof e === 'string') {
        setError(e);
      } else if (e && typeof e === 'object' && 'detail' in e) {
        setError(String(e.detail));
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const dismissError = () => {
    setError('');
  };

  return (
    <PageTransition key="signup">
      <AuthLayout
        visualComponent={<VisualSideLayout />}
        formComponent={
          <div className="flex flex-col py-8 w-full md:w-2/3">
            <FeedbackMessage
              feedback={feedback ? feedback.message : ''}
              title={feedback.title}
              handleClose={dismissError}
            />
            <ErrorMessage
              error={error ? error : ''}
              handleClose={dismissError}
            />
            <Notifications
              position={isMobile ? 'top-center' : 'bottom-right'}
              limit={isMobile ? 1 : 5}
            />
            <SignUpForm
              handleSignin={navigateToSignin}
              onSubmit={signUp}
              loading={loading}
              error={error}
            />
          </div>
        }
      />
    </PageTransition>
  );
};

export default SignUp;
